import { TicketItem } from "@features/cartDetails/types";
import { SparklesIcon } from "@heroicons/react/20/solid";
import { useNotificationStore } from "@store/notificationStore";
import { PriceStatus } from "@utils/enums";
import { formatPrice, joinClassNames } from "@utils/helpers";
import { motion } from "framer-motion";
import { useState } from "react";
import AddTicketDropdown from "./addTicketDropdown";
import FlexiPricePopover from "./flexiPricePopover";
import MobileTicketDisplay from "./mobileTicketDisplay";
import { FormattedPrice } from "@features/eventDisplayGA/types";
import { handleAddCart } from "@features/eventDisplayGA/utils/cartUtils";
import { Badge, Card, Text } from "@components/theming";
import { useLocale, useTranslations } from "next-intl";
import { useSWRConfig } from "swr";
import { useIsShopsite } from "@context/DomainContext";
import { Analytics, EventLandingPage } from "@types";

interface Props {
  prices: FormattedPrice[];
  eventId: number;
  eventName: string;
  eventVenueId: number;
  orgUid: string;
  showingId: number;
  basketTickets: TicketItem[];
  analyticsConfigs?: Analytics[];
  landingPages?: EventLandingPage[];
}

const EventGASelection = ({
  prices,
  eventId,
  eventName,
  eventVenueId,
  orgUid,
  showingId,
  basketTickets,
  analyticsConfigs,
  landingPages,
}: Props) => {
  const { cache } = useSWRConfig();
  const t = useTranslations("EventPage.gaselection");
  const notification_translations = useTranslations("EventPage.notifications");
  const [hoveredBadge, setHoveredBadge] = useState<string | null>(null);
  const { setFailure, setSuccess } = useNotificationStore((state) => state);
  const locale = useLocale();
  const isShopsite = useIsShopsite();

  const cache_key = Array.from(cache.keys()).filter((key) =>
    key.includes(
      `events/${eventId}/${eventVenueId}/showings/${showingId}/tickets/ga`
    )
  );

  const selected_quantities = prices.map((p) => ({
    id: p.id,
    qty: p.bookedTickets || 0,
    qty_from_combo: p.bookedFromCombo,
  }));

  const putQtyInCart = async (
    price: FormattedPrice,
    qty: string,
    isDynamic?: boolean
  ) => {
    return handleAddCart(
      eventId,
      eventName,
      eventVenueId,
      orgUid,
      showingId,
      selected_quantities.find((sq) => sq.id === price.id)?.qty,
      setSuccess,
      setFailure,
      basketTickets,
      price,
      qty,
      isDynamic,
      {
        removedcombo: {
          success: notification_translations("removedcombo.success"),
          failure: notification_translations("removedcombo.failure"),
        },
        removedtickets: {
          success: notification_translations("removedtickets.success"),
          failure: notification_translations("removedtickets.failure"),
        },
        addedtickets: {
          success: notification_translations("addedtickets.success"),
          failure: notification_translations("addedtickets.failure"),
        },
      },
      cache.get(cache_key[0])?.data,
      analyticsConfigs,
      landingPages
    );
  };

  return (
    <div className="relative">
      <Card
        size="2"
        sharedClasses="max-sm:hidden"
        shopsiteClasses="border bg-white rounded-lg"
        whitelabelClasses="!p-0"
      >
        <div
          className={joinClassNames(
            "flex p-4 w-full border-b",
            isShopsite ? "" : "border-[color:var(--gray-5)]"
          )}
        >
          <Text
            as="div"
            size="2"
            weight="medium"
            shopsiteClasses="text-sm font-medium"
            sharedClasses="w-36 mr-4"
          >
            {t("type")}
          </Text>
          <Text
            as="div"
            size="2"
            weight="medium"
            shopsiteClasses="text-sm font-medium"
            sharedClasses="block flex-1"
          >
            {t("info")}
          </Text>
          <Text
            as="div"
            size="2"
            weight="medium"
            shopsiteClasses="text-sm font-medium"
            sharedClasses="text-sm w-32 mr-4 text-right"
          >
            {t("price")}
          </Text>
          <div className="w-16" />
        </div>
        <div
          className={joinClassNames(
            "block divide-y",
            isShopsite ? "" : "divide-[color:var(--gray-5)]"
          )}
        >
          {prices
            ?.sort((a, b) => a.order - b.order)
            .map((price, index) => (
              <div
                key={price.id}
                className={joinClassNames(
                  "flex items-center justify-between p-4 w-full",
                  (
                    price.isCombo
                      ? price.max === 0 || price.max === price.bookedTickets
                      : (price.status !== PriceStatus.AVAILABLE &&
                          selected_quantities.find((sq) => sq.id === price.id)
                            ?.qty === 0) ||
                        (price.bookingLimit ?? price.max) < price.min
                  )
                    ? isShopsite
                      ? "bg-gray-100"
                      : "bg-[color:var(--gray-a3)]"
                    : "",
                  index === prices.length - 1 && isShopsite
                    ? "rounded-b-[7px]"
                    : ""
                )}
              >
                <Text
                  as="div"
                  size="2"
                  weight="bold"
                  sharedClasses="text-left w-36 mr-4"
                  shopsiteClasses="text-sm font-semibold"
                >
                  <span className="flex flex-col gap-0.5 w-fit">
                    <Text
                      as="span"
                      whitelabelClasses={
                        (
                          price.isCombo
                            ? price.max === 0 ||
                              price.max === price.bookedTickets
                            : (price.status !== PriceStatus.AVAILABLE &&
                                selected_quantities.find(
                                  (sq) => sq.id === price.id
                                )?.qty === 0) ||
                              (price.bookingLimit ?? price.max) < price.min
                        )
                          ? "text-[color:var(--gray-11)]"
                          : ""
                      }
                    >
                      {price.type}
                    </Text>
                    {price.isCombo ? (
                      price.max === 0 ? (
                        <Badge
                          color="red"
                          shopsiteClasses="!py-1 !rounded-lg w-fit"
                        >
                          <Text
                            size="2"
                            weight="bold"
                            shopsiteClasses="text-sm font-semibold"
                          >
                            {t("soldout")}
                          </Text>
                        </Badge>
                      ) : price.max === price.bookedTickets ? (
                        <Badge
                          color="red"
                          shopsiteClasses="!py-1 !rounded-lg w-fit"
                        >
                          <Text
                            size="2"
                            weight="bold"
                            shopsiteClasses="text-sm font-semibold"
                          >
                            {t("maxbought")}
                          </Text>
                        </Badge>
                      ) : null
                    ) : (
                      ((price.status !== PriceStatus.AVAILABLE &&
                        selected_quantities.find((sq) => sq.id === price.id)
                          ?.qty === 0) ||
                        (price.bookingLimit ?? price.max) < price.min) && (
                        <Badge
                          color="red"
                          sharedClasses="w-fit"
                          shopsiteClasses="!py-1 !rounded-lg"
                        >
                          <Text
                            size="2"
                            weight="bold"
                            shopsiteClasses="text-sm font-semibold"
                          >
                            {price.status === PriceStatus.NOTAVAILABLE
                              ? t("notavailable")
                              : price.status === PriceStatus.SOLDOUT
                              ? t("soldout")
                              : price.status === PriceStatus.BOOKEDOUT
                              ? t("bookedout")
                              : t("limitreached")}
                          </Text>
                        </Badge>
                      )
                    )}
                  </span>
                </Text>
                <div
                  className={joinClassNames(
                    "flex flex-col gap-2 line-clamp-2 flex-1 text-left mr-4 text-sm",
                    isShopsite ? "text-gray-500" : "text-[color:var(--gray-11)]"
                  )}
                >
                  {price.info ? <span>{price.info}</span> : <></>}
                  <div
                    className={joinClassNames(
                      "flex gap-2",
                      !isShopsite ? "flex-wrap" : ""
                    )}
                  >
                    {price.isPromo ? (
                      <Badge
                        asChild
                        color="yellow"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-promo` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-promo`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-promo`
                              ? {
                                  flexShrink: 0,
                                }
                              : {
                                  flexShrink: 1,
                                }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="flex gap-1 items-center w-fit truncate"
                          >
                            <SparklesIcon className="h-4 w-4" />
                            {t("promo")}
                            <SparklesIcon className="h-4 w-4" />
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {price.isCombo ? (
                      <Badge
                        asChild
                        color="green"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-combo` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-combo`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-combo`
                              ? {
                                  flexShrink: 0,
                                }
                              : {
                                  flexShrink: 1,
                                }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="w-fit truncate"
                          >
                            {t("combo")}
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {price.areaName ? (
                      <Badge
                        asChild
                        color="sky"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-area` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-area`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-area`
                              ? {
                                  flexShrink: 0,
                                }
                              : {
                                  flexShrink: 1,
                                }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="w-fit truncate"
                          >
                            {price.areaName}
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {price.priceSchemeName ? (
                      <Badge
                        asChild
                        color="cyan"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-price_scheme` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-price_scheme`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-price_scheme`
                              ? {
                                  flexShrink: 0,
                                }
                              : {
                                  flexShrink: 1,
                                }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="w-fit truncate"
                          >
                            {price.priceSchemeName}
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {price.priceSectionName ? (
                      <Badge
                        asChild
                        color="teal"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-price_section` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-price_section`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-price_section`
                              ? {
                                  flexShrink: 0,
                                }
                              : {
                                  flexShrink: 1,
                                }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="w-fit truncate"
                          >
                            {price.priceSectionName}
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {price.priceAgeStartDate &&
                    price.status !== PriceStatus.AVAILABLE ? (
                      <Badge
                        asChild
                        color="amber"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-start-date` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-start-date`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-start-date`
                              ? {
                                  flexShrink: 0,
                                }
                              : {
                                  flexShrink: 1,
                                }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="w-fit truncate"
                          >
                            {t("availability", {
                              preposition: "from",
                              date: price.priceAgeStartDate,
                            })}
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {price.priceAgeEndDate &&
                    price.status !== PriceStatus.AVAILABLE ? (
                      <Badge
                        asChild
                        color="amber"
                        size="2"
                        sharedClasses="cursor-default"
                        shopsiteClasses={joinClassNames(
                          "!rounded-lg truncate font-semibold w-fit transition-opacity ease-in-out duration-200",
                          hoveredBadge === `${price.id}-end-date` ||
                            hoveredBadge === null ||
                            !hoveredBadge.includes(price.id)
                            ? "opacity-100"
                            : "opacity-70"
                        )}
                      >
                        <motion.span
                          onHoverStart={() =>
                            setHoveredBadge(`${price.id}-end-date`)
                          }
                          onHoverEnd={() => setHoveredBadge(null)}
                          animate={
                            hoveredBadge === `${price.id}-end-date`
                              ? {
                                  flexShrink: 0,
                                }
                              : {
                                  flexShrink: 1,
                                }
                          }
                        >
                          <Text
                            as="span"
                            size="2"
                            weight="bold"
                            sharedClasses="w-fit truncate"
                          >
                            {t("availability", {
                              preposition: "until",
                              date: price.priceAgeEndDate,
                            })}
                          </Text>
                        </motion.span>
                      </Badge>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
                {price.priceMaximum !== null &&
                price.priceMaximum > 0 &&
                price.priceMinimum !== null &&
                price.priceMinimum > 0 ? (
                  <span className="relative w-32 text-right text-sm tabular-nums">
                    <FlexiPricePopover
                      price={price}
                      selectedQuantities={selected_quantities}
                      putQtyInCart={putQtyInCart}
                    />
                  </span>
                ) : (
                  <>
                    <span className="relative w-32 pr-4 text-right text-sm tabular-nums">
                      {price.isCombo ? (
                        <div className="flex flex-col items-end">
                          <Text as="span">
                            {formatPrice(
                              locale,
                              price.currency.CurrencyCode,
                              price.discountPrice
                            )}
                          </Text>
                          {price.price > price.discountPrice ? (
                            <Text
                              as="span"
                              sharedClasses="text-xs font-semibold line-through"
                              whitelabelClasses="text-[color:var(--gray-9)]"
                              shopsiteClasses="text-gray-400"
                            >
                              {formatPrice(
                                locale,
                                price.currency.CurrencyCode,
                                price.price
                              )}
                            </Text>
                          ) : (
                            <></>
                          )}
                        </div>
                      ) : (
                        <Text as="span">
                          {formatPrice(
                            locale,
                            price.currency.CurrencyCode,
                            price.price
                          )}
                        </Text>
                      )}
                    </span>
                    <AddTicketDropdown
                      price={price}
                      putQtyInCart={putQtyInCart}
                      selectedQuantities={selected_quantities}
                    />
                  </>
                )}
              </div>
            ))}
        </div>
      </Card>
      <div className="flex flex-col gap-4 sm:hidden">
        {prices.map((price) => (
          <MobileTicketDisplay
            key={price.id}
            price={price}
            putQtyInCart={putQtyInCart}
            selectedQuantities={selected_quantities}
          />
        ))}
      </div>
    </div>
  );
};

export default EventGASelection;
